// web/src/admin/adminNav.tsx — the operator nav (portal spec 22 §4d), shared by the desktop sidebar (AppShell) and the
// phone strip at the top of every admin page (AdminGate), plus the badge counts (GET /v1/admin/counts, every 60 s).
// Builds (QA) and Site updates (chalice tasks) are not here: hidden with spec 10's flags. Health and Signals (the
// chalice-v2 console) stay reachable at the end.
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminCounts } from '../lib/types';
import { Icon } from '../components/Icon';
import { CountPill } from './adminUi';

export type BadgeKey = keyof AdminCounts;
export interface AdminNavItem { to: string; label: string; icon: string; badge?: BadgeKey }

export const ADMIN_NAV: AdminNavItem[] = [
  { to: '/admin', label: 'Clients', icon: 'building' }, // the board
  { to: '/admin/leads', label: 'Leads', icon: 'inbox', badge: 'leads' },
  { to: '/admin/lifecycle', label: 'Lifecycle', icon: 'rocket', badge: 'lifecycle' },
  { to: '/admin/outbox', label: 'Outbox', icon: 'send', badge: 'outbox' },
  { to: '/admin/review', label: 'Review', icon: 'file', badge: 'review' },
  { to: '/admin/runs', label: 'Monthly runs', icon: 'clock' },
  { to: '/admin/money', label: 'Money', icon: 'bar-chart' },
  { to: '/admin/customers', label: 'Billing', icon: 'credit-card' },
  { to: '/admin/users', label: 'Logins', icon: 'users' },
  { to: '/admin/health', label: 'Health', icon: 'alert' },
  { to: '/admin/signals', label: 'Signals', icon: 'list' },
];

export function useAdminCounts(enabled = true) {
  return useQuery({ queryKey: ['admin-counts'], queryFn: () => api.adminCounts(), enabled, refetchInterval: 60_000 });
}

/** Phone only: the operator nav as one row of pills that scrolls inside itself (the page never scrolls sideways). */
export function AdminTabs() {
  const counts = useAdminCounts().data;
  return (
    <nav aria-label="Admin" className="-mx-4 mb-4 overflow-x-auto px-4 md:hidden">
      <div className="flex w-max gap-2">
        {ADMIN_NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/admin'}
            className={({ isActive }) => `inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm ${isActive ? 'border-run bg-run/10 text-ink' : 'border-hair text-muted'}`}>
            <Icon name={n.icon} size={15} />{n.label}
            {n.badge && <CountPill n={counts?.[n.badge]} />}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
